import { useState, useRef } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Paperclip, X } from "lucide-react";
import { careersApply } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../ui/dialog";

export default function ApplyDialog({ role, light = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", portfolio: "", message: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Resume must be under 5MB."); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("role", role.title);
      fd.append("portfolio", form.portfolio);
      fd.append("message", form.message);
      if (file) fd.append("resume", file);
      await careersApply(fd);
      toast.success("Application received! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", portfolio: "", message: "" });
      setFile(null);
      setOpen(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={`apply-${role.id}`}
          className={`${light ? "bg-white text-primary hover:bg-white/90" : "bg-brand-gradient text-white border-0 hover:opacity-90"} rounded-full h-12 px-7 shrink-0 ${className}`}
        >
          Apply <ArrowUpRight className="ml-1 h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Apply · {role.title}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Application form for {role.title}</DialogDescription>

        <form data-testid="apply-form" onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="a-name">Full name *</Label>
              <Input data-testid="apply-name" id="a-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="a-email">Email *</Label>
              <Input data-testid="apply-email" id="a-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-xl h-11" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="a-phone">Phone</Label>
              <Input data-testid="apply-phone" id="a-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-xl h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="a-portfolio">Portfolio / LinkedIn</Label>
              <Input data-testid="apply-portfolio" id="a-portfolio" value={form.portfolio} onChange={(e) => set("portfolio", e.target.value)} placeholder="https://..." className="rounded-xl h-11" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="a-message">Why VYFT?</Label>
            <Textarea data-testid="apply-message" id="a-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us a little about you..." className="rounded-xl min-h-[100px]" />
          </div>
          <div className="space-y-2">
            <Label>Resume / CV <span className="text-muted-foreground font-normal">(PDF or Word, max 5MB)</span></Label>
            <input ref={fileRef} data-testid="apply-resume" type="file" accept=".pdf,.doc,.docx" onChange={pickFile} className="hidden" />
            {file ? (
              <div className="flex items-center justify-between rounded-xl border border-border/15 bg-background/50 px-4 py-3">
                <span className="flex items-center gap-2 text-sm truncate"><Paperclip className="h-4 w-4 text-primary shrink-0" /> {file.name}</span>
                <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <button type="button" data-testid="apply-resume-btn" onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-2 rounded-xl border border-dashed border-border/25 bg-background/40 px-4 py-3 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                <Paperclip className="h-4 w-4" /> Attach your resume
              </button>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="apply-submit" type="submit" disabled={loading} className="rounded-full bg-brand-gradient text-white border-0 hover:opacity-90">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
